import { forwardRef } from "react";
import styled, { keyframes } from "styled-components";

import { IconButton, type IconButtonProps, type IconButtonSize } from "../IconButton";
import { controlTransition } from "../foundations/controlStyles";

const legacyHueCycle = keyframes`
  from {
    filter: hue-rotate(0deg);
  }

  to {
    filter: hue-rotate(360deg);
  }
`;

const StyledTrackingPlayButton = styled(IconButton)`
  ${controlTransition("filter", "transform")}

  /* SigmaKit GUI와 training-infra가 공유하던 시각 식별자다. gradient/주기는 의도적으로
     보존하되 focus/disabled/크기 계약은 기반 IconButton에 위임한다. */
  &:not(:disabled) {
    border-color: ${({ theme }) => theme.color.border.emphasis};
    background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
    background-image: ${({ theme }) => theme.color.bg.control.trackingPlay};
    color: ${({ theme }) => theme.color.text.default};
    animation: ${legacyHueCycle} 2.2s linear infinite;
  }

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.color.border.emphasis};
    background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
    background-image: ${({ theme }) => theme.color.bg.control.trackingPlay};
    color: ${({ theme }) => theme.color.text.default};
    filter: saturate(1.2) brightness(1.08);
    transform: translateY(-1px);
  }

  &:not(:disabled):active {
    border-color: ${({ theme }) => theme.color.border.emphasis};
    background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
    background-image: ${({ theme }) => theme.color.bg.control.trackingPlay};
    color: ${({ theme }) => theme.color.text.default};
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    &:not(:disabled),
    &:not(:disabled):hover,
    &:not(:disabled):active {
      animation: none;
      transform: none;
    }
  }

  @media (forced-colors: active) {
    &:not(:disabled) {
      border-color: ButtonText;
      background-color: ButtonFace;
      background-image: none;
      color: ButtonText;
      animation: none;
    }
  }
`;

export interface TrackingPlayButtonProps extends Omit<
  IconButtonProps,
  "border" | "size" | "tone" | "variant"
> {
  size?: IconButtonSize;
}

/**
 * SigmaKit의 legacy Tracking Play 식별자를 c6 IconButton 계약 위에 보존한 실행 버튼.
 */
export const TrackingPlayButton = forwardRef<HTMLButtonElement, TrackingPlayButtonProps>(
  ({ size = "md", ...props }, ref) => (
    <StyledTrackingPlayButton
      ref={ref}
      {...props}
      size={size}
      variant="surface"
      tone="primary"
      border
    />
  ),
);

TrackingPlayButton.displayName = "TrackingPlayButton";
